import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AccordionCont,
  CloseOpenIconCont,
  SurveyInfoCont,
  SurveyInfoTitle,
  SurveysInfoDesc,
  SurveysInfoStats,
  RemoveIcon,
} from './SurveyAccordion.styles';
import { useAccordion } from '../../hooks/useAccordion';
import { useScroll } from '../../hooks/useScroll';
import { IGenerateContentsArray, ITopic } from '../../hooks/useSurveyContents';
import { ISurveyContentsItem } from '../../services/surveys';
import Accordion from '../Accordion/Accordion';
import CloseIcon from '@/assets/icons/closeAllAccordion.svg?react';
import CircleMinus from '@/assets/icons/minusInCircle.svg?react';
import OpenIcon from '@/assets/icons/openAllAccordion.svg?react';

interface ISurveyAccordion {
  language: string;
  title: string;
  insertedCategoryId: string | null;
  item: IGenerateContentsArray;
  revealedCategories: { [k: string]: boolean };
  isEffect?: boolean;
  onChangeCategoryAssignment: (id: string) => void;
  onSetScope: (code: string, scope: { code: string; items: ISurveyContentsItem[] }[]) => void;
  onSetRevealedCategories: (code: string, isCategoryRevealed: boolean) => void;
  setIsTransitionBlocked: React.Dispatch<React.SetStateAction<boolean>>;
  changeCategoryAssigment: (id: string) => void;
}

type TSurveyAccordionTopic = Omit<ITopic, 'shortDescription' | 'title'> & {
  title: string | Record<string, string>;
  shortDescription?: string;
  name?: string;
  selected?: boolean;
};

const SurveyAccordion: FC<ISurveyAccordion> = ({
  language,
  title,
  item,
  insertedCategoryId,
  revealedCategories,
  isEffect,
  onSetScope,
  onSetRevealedCategories,
  setIsTransitionBlocked,
  changeCategoryAssigment,
}) => {
  const { code, id, name, description, numberOfTopics, topics } = item;
  const { t } = useTranslation();
  const {
    itemsArr,
    setItemStatus,
    assignedItemsArr,
    notAssignedItemsArr,
    changeItemAssigment,
    changeMultipleItemsAssignment,
  } = useAccordion<TSurveyAccordionTopic>(topics as unknown as TSurveyAccordionTopic[]);

  const { isIntersecting } = useScroll(title);
  const [isExpandedCategory, setIsExpandedCategory] = useState(false);

  const numberOfAssignedItems = assignedItemsArr.length;
  const numberOfAssignedQuestions = assignedItemsArr.reduce((acc, { items }) => {
    return acc + items.length;
  }, 0);

  const handleCloseOpenCategory = () => {
    setIsExpandedCategory(isExpandedCategory => !isExpandedCategory);
    onSetRevealedCategories(code, false);
  };

  useEffect(() => {
    if (revealedCategories[code]) {
      setIsExpandedCategory(revealedCategories[code]);
    }
  }, [code, revealedCategories]);

  useEffect(() => {
    onSetScope(code, assignedItemsArr);

    return () => {
      onSetScope(code, []);
    };
  }, [assignedItemsArr, code, onSetScope]);

  useEffect(() => {
    if (insertedCategoryId === id && itemsArr.length >= 0) {
      const ids = itemsArr
        .filter(item => (item as { importance?: string }).importance === 'recommended')
        .map(({ id }) => id);

      changeMultipleItemsAssignment(ids);
    }
  }, [id, insertedCategoryId, itemsArr.length]);

  return (
    <AccordionCont id={title} active={isIntersecting}>
      <CloseOpenIconCont data-testid="closeOpenAllButton" onClick={handleCloseOpenCategory}>
        {isExpandedCategory ? <CloseIcon /> : <OpenIcon />}
      </CloseOpenIconCont>
      <SurveyInfoCont id="SurveyInfoCont">
        <SurveyInfoTitle id="SurveyInfoTitle">
          {name}
          {isEffect ? null : (
            <RemoveIcon>
              <CircleMinus onClick={() => changeCategoryAssigment(item.id)} />
            </RemoveIcon>
          )}
        </SurveyInfoTitle>
        <SurveysInfoStats id="SurveysInfoStats">
          {t('viewSurvey.contents.topics', { lng: language })} :{' '}
          <b>
            {numberOfAssignedItems} of {numberOfTopics}{' '}
          </b>
          {t('viewSurvey.contents.questions', { lng: language })} :{' '}
          <b>{numberOfAssignedQuestions}</b>
        </SurveysInfoStats>
        <SurveysInfoDesc id="SurveysInfoDesc">
          <div dangerouslySetInnerHTML={{ __html: description || '' }} />
        </SurveysInfoDesc>
      </SurveyInfoCont>
      {isExpandedCategory ? (
        <Accordion
          title={title}
          setItemStatus={setItemStatus}
          assignedItemsArr={assignedItemsArr}
          notAssignedItemsArr={notAssignedItemsArr}
          changeItemAssigment={changeItemAssigment}
          setIsTransitionBlocked={setIsTransitionBlocked}
        />
      ) : null}
    </AccordionCont>
  );
};

export default SurveyAccordion;
